import type { IConfigAPI } from '../../interface/config';
import type { IProgressPromise, IProgressPromiseGenerator } from '../../interface/promise';
import type { IFetcher } from '../../interface/util';
import type { IMediaURLResolverParameters, MediaBinaryStorageTypeMap } from '../interface/media';
import mediaDownload from './media.download';
import mediaUtil from './media.util';

const _hasHttpPrefix = (url?: string) => url?.startsWith('http://') || url?.startsWith('https://');

function _requestFromConfig(config: Partial<IConfigAPI>, mediaId: string): string | undefined {
    if (!config.extension?.media) {
        return undefined;
    }

    const templatedPath = _hasHttpPrefix(config.extension?.media)
        ? config.extension?.media
        : `${config.url}${config.extension?.media}`;

    const resolvedPath = templatedPath ? templatedPath.replace('%MEDIAID%', mediaId) : undefined;

    return _hasHttpPrefix(resolvedPath) ? resolvedPath : undefined;
}

function _requestFromConfigWithToken(
    config: Partial<IConfigAPI>,
    mediaId: string,
    accessToken: string,
): string | undefined {
    if (!config.extension?.mediaWithAccessToken) {
        return undefined;
    }

    const templatedPath = _hasHttpPrefix(config.extension?.mediaWithAccessToken ?? '')
        ? config.extension?.mediaWithAccessToken
        : `${config.url ?? ''}${config.extension?.mediaWithAccessToken}`;

    const resolvedPath = templatedPath
        ? templatedPath.replace('%MEDIAID%', mediaId).replace('%ACCESS_TOKEN%', accessToken)
        : undefined;

    return _hasHttpPrefix(resolvedPath) ? resolvedPath : undefined;
}

function canResolveMedium(mediaId: string): Promise<boolean> {
    /* No way to query for this, just assume we can */
    return Promise.resolve(true);
}

function requestURL(
    configs: Array<Partial<IConfigAPI>>,
    mediaId: string,
    parameters: IMediaURLResolverParameters = {},
): Array<string> {
    let resolver = (mediaId: string, config: Partial<IConfigAPI>) =>
        _requestFromConfig(config, mediaId);

    const authToken = parameters.accessToken;

    if (authToken) {
        resolver = (mediaId: string, config: Partial<IConfigAPI>) =>
            _requestFromConfigWithToken(config, mediaId, authToken);
    }

    const urls = configs
        .map(config => resolver(mediaId, config))
        .filter(config => config) as Array<string>;

    return urls;
}

function getMedia<
    K extends keyof MediaBinaryStorageTypeMap,
    V extends MediaBinaryStorageTypeMap[K],
>(
    promiser: IProgressPromiseGenerator,
    config: Array<Partial<IConfigAPI>>,
    fetcher: Promise<IFetcher>,
    type: K,
    mediaId: string,
    parameters: IMediaURLResolverParameters,
): IProgressPromise<V> {
    const urls = requestURL(config, mediaId, parameters);
    const requests = urls.map(url => mediaUtil.requestForURL(url, type));

    return mediaDownload.download(promiser, fetcher, requests, type);
}

const module = {
    canResolveMedium,
    getMedia,
    requestURL,
};

export default module;
